import produce from 'immer';
import { isEmpty } from 'lodash';
import { StateCreator } from 'zustand';

import bookingsAPI from '../services/api/bookings';
import userAPI from '../services/api/user';
import userDocumentsAPI from '../services/api/user-documents';
import { IUserDocument } from '../shared/ts/document';
import { IGuest, IGuestCreated, IUser } from '../shared/ts/user';

export interface IUserSlice {
  user: IUser | null;
  guests: IGuest[];
  guest: IGuest | null;
  userDocuments: IUserDocument[];
  getUserById: (id: number) => Promise<void>;
  updateUserById: (id: number, payload: IUser) => Promise<void>;
  getUserDocuments: (id: number, type: string[]) => Promise<void>;
  postUserDocument: (
    payload: IUserDocument,
    onUploadProgress: (progressEvent: ProgressEvent) => void,
  ) => Promise<void>;

  postGuestDocument: (
    payload: IUserDocument,
    onUploadProgress: (progressEvent: ProgressEvent) => void,
  ) => Promise<void>;

  deleteUserDocument: (
    id: number,
    documentSide?: string,
    booking_id?: string,
  ) => Promise<void>;

  deleteGuestDocument: (
    guest_id: number,
    id: number,
    documentSide?: string,
    booking_id?: string,
  ) => Promise<void>;
  getGuests: (booking_id: string) => Promise<void>;
  getGuestDocuments: (guest_id: string, booking_id: string) => Promise<void>;
  createGuestByBookingId: (
    booking_id: string,
    payload: IGuest,
  ) => Promise<IGuestCreated>;

  updateGuestById: (
    booking_id: string,
    guest_id: string,
    payload: IGuest,
  ) => Promise<void>;

  deleteGuestById: (booking_id: string, user_id: number) => Promise<void>;
}

const createUserSlice: StateCreator<IUserSlice, [], []> = (set, get) => ({
  user: null,
  updateUser: null,
  userDocuments: [],
  guests: [],
  guest: null,
  guestDocuments: [],
  //user
  getUserById: async (id: number) => {
    try {
      set({ user: await userAPI.getUserById(id) });
    } catch (err) {
      throw err;
    }
  },
  updateUserById: async (id: number, payload: IUser) => {
    try {
      set({ user: await userAPI.updateUserById(id, payload) });
    } catch (err) {
      throw err;
    }
  },

  //user documents
  getUserDocuments: async (id: number, type: string[]) => {
    try {
      set({ userDocuments: await userDocumentsAPI.getUserDocuments(id, type) });
    } catch (err) {
      throw err;
    }
  },

  postUserDocument: async (
    payload: IUserDocument,
    onUploadProgress: (progressEvent: ProgressEvent) => void,
  ) => {
    try {
      const newDocument = await userDocumentsAPI.postUserDocument(
        payload,
        onUploadProgress,
      );

      const documentToUpdateIndex = get().userDocuments.findIndex(
        doc => doc.id == newDocument.id,
      );

      set(
        produce(state => {
          if (documentToUpdateIndex !== -1) {
            state.userDocuments.splice(documentToUpdateIndex, 1, newDocument);
          } else state.userDocuments.push(newDocument);
        }),
      );
    } catch (err) {
      throw err;
    }
  },

  postGuestDocument: async (
    payload: IUserDocument,
    onUploadProgress: (progressEvent: ProgressEvent) => void,
  ) => {
    try {
      const { ...document } = await userDocumentsAPI.postUserDocument(
        payload,
        onUploadProgress,
      );
      const guestToUpdateIndex = get().guests?.findIndex(
        guest => Number(guest.user_id) === Number(payload.user_id),
      );

      const documentToUpdateIndex = get().guests[
        guestToUpdateIndex
      ]?.documents?.findIndex(doc => Number(doc.id) === Number(document.id));
      set(
        produce(state => {
          if (documentToUpdateIndex !== -1) {
            state.guests[guestToUpdateIndex].documents.splice(
              documentToUpdateIndex,
              1,
              document,
            );
          } else state.guests[guestToUpdateIndex].documents?.push(document);
        }),
      );
    } catch (err) {
      console.log(err);
      throw err;
    }
  },

  deleteUserDocument: async (id: number, documentSide?: string) => {
    try {
      const document = await userDocumentsAPI.deleteUserDocument(
        id,
        documentSide,
      );

      const isDocumentToUpdate = !isEmpty(document);
      set(state => ({
        userDocuments: isDocumentToUpdate
          ? state.userDocuments.map(doc =>
              doc.id === id ? { id, ...document } : doc,
            )
          : state.userDocuments.filter(doc => doc.id != id),
      }));
    } catch (err) {
      throw err;
    }
  },

  deleteGuestDocument: async (
    user_id: number,
    id: number,
    documentSide?: string,
    booking_id?: string,
  ) => {
    try {
      const document = await userDocumentsAPI.deleteUserDocument(
        id,
        documentSide,
        booking_id,
      );
      const guestIndex = get().guests.findIndex(
        guest => Number(guest.user_id) === user_id,
      );
      const documentToUpdateIndex = get().guests[
        guestIndex
      ]?.documents?.findIndex(doc => doc.id === id);

      set(
        produce(state => {
          if (documentToUpdateIndex !== -1) {
            state.guests[guestIndex].documents?.splice(
              documentToUpdateIndex!,
              1,
              document,
            );
          } else state.guests[guestIndex].documents?.push(document);
        }),
      );
    } catch (err) {
      console.log(err);
      throw err;
    }
  },
  getGuests: async (booking_id: string) => {
    try {
      set({ guests: await bookingsAPI.getGuests(booking_id) });
    } catch (err) {
      throw err;
    }
  },

  getGuestDocuments: async (guest_id: string, booking_id: string) => {
    try {
      const documents = await bookingsAPI.getGuestDocuments(
        guest_id,
        booking_id,
      );

      const guestToUpdateIndex = get().guests.findIndex(
        guest => guest_id === guest.id || guest_id === guest.guest_id,
      );
      set(
        produce(state => {
          state.guests[guestToUpdateIndex as number].documents = documents;
        }),
      );
    } catch (err) {
      throw err;
    }
  },

  createGuestByBookingId: async (booking_id: string, payload: IGuest) => {
    try {
      const createdUser = await bookingsAPI.createGuest(booking_id, payload);
      const newGuest = {
        user_id: createdUser.user_id,
        guest_id: createdUser.id,
        user_email: payload.contacts?.email,
        ...payload,
      };
      set(state => ({
        guests: [...state.guests, { ...(newGuest as IGuest) }],
      }));

      return createdUser;
    } catch (err) {
      throw err;
    }
  },
  updateGuestById: async (
    booking_id: string,
    guest_id: string,
    payload: IGuest,
  ) => {
    try {
      set({
        guest: await bookingsAPI.updateGuestById(booking_id, guest_id, payload),
      });
    } catch (err) {
      throw err;
    }
  },
  deleteGuestById: async (booking_id: string, user_id: number) => {
    try {
      await bookingsAPI.deleteGuestById(booking_id, user_id);
      set(state => ({
        guests: state.guests.filter(
          guest => Number(guest.user_id) !== Number(user_id),
        ),
      }));
    } catch (err) {
      throw err;
    }
  },
});

export default createUserSlice;
